/**
 * Portfolio X-Ray API service - Contributions only
 *
 * Note: The X-Ray table view uses the existing screener /search endpoint
 * with portfolio_lookthrough_id parameter.
 */

import { apiRequest } from "../client";

// ============================================================================
// Types
// ============================================================================

export interface XRayContributionField {
  id: string;
  name: string;
  field_path: string;
  source_path: string;
  aggregation_method: string;
  data_type: string;
  formatting?: {
    type?: string;
    options?: Record<string, any>;
  };
}

export interface XRayExposureGroup {
  id: string;
  name: string;
  categories: string[];
}

export interface XRayContributionOptionsResponse {
  fundamental_fields: XRayContributionField[];
  exposure_groups: XRayExposureGroup[];
}

export type HoldingsSource = "latest" | "target" | "draft";

export interface XRayContributionRequest {
  field?: string;
  exposure?: string;
  group_by?: string;
  as_at?: string;
  holdings_source?: HoldingsSource;
}

export interface XRayContributionRow {
  name: string;
  resource_id: string;
  resource_type: string;
  weight: number;
  value?: number | null;
  contribution?: number | null;
  pct_of_total?: number | null;
  // For exposure mode - dynamic keys
  [key: string]: string | number | null | undefined;
}

export interface XRayContributionResponse {
  mode: "field" | "exposure";
  field?: string;
  exposure?: string;
  aggregation_method?: string;
  categories?: string[];
  total?: number;
  totals?: Record<string, number>;
  rows: XRayContributionRow[];
  as_at: string;
  holdings_source?: string;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get available contribution options (fields and exposures)
 */
export async function getXRayContributionOptions(
  portfolioId: string,
  asAt?: string
): Promise<XRayContributionOptionsResponse> {
  const params = asAt ? `?as_at=${asAt}` : "";
  return apiRequest<XRayContributionOptionsResponse>({
    method: "GET",
    url: `/portfolios/${portfolioId}/xray/contribution-options${params}`,
  });
}

/**
 * Get contribution analysis for a field or exposure
 */
export async function getXRayContribution(
  portfolioId: string,
  request: XRayContributionRequest
): Promise<XRayContributionResponse> {
  return apiRequest<XRayContributionResponse>({
    method: "POST",
    url: `/portfolios/${portfolioId}/xray/contribution`,
    data: request,
  });
}
